import React, { useCallback, useState } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';

import { MODEL_DASHBOARD_COLUMNS } from '@mpm-druid/constants';
import { Panel, PanelSectionLayout } from '@mpm-druid/types';
import { useObserveResizeNode } from '@shared/hooks';
import { useModelViewStore } from '../modelViewStore';
import { PanelGridItem } from './PanelGridItem';

import styles from './PanelGrid.module.scss';

const ROW_HEIGHT = 302;
const LAYOUT_SETTING_MARGIN: [number, number] = [10, 10];

type PanelGridProps = {
  layout: PanelSectionLayout;
  panels: Panel[];
  sectionId: string;
};

function PanelGridComponent({ layout, panels, sectionId }: PanelGridProps) {
  const editSectionLayout = useModelViewStore(state => state.editSectionLayout);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const gridRef = useObserveResizeNode(node => {
    setContainerWidth(node.clientWidth);
  });

  const handleLayoutChange = useCallback(
    (newLayout: Layout[]) => {
      editSectionLayout(sectionId, convertLayout(newLayout));
    },
    [editSectionLayout, sectionId]
  );

  if (!containerWidth) {
    return <div ref={gridRef} className={styles.placeholderContainer} />;
  }

  return (
    <div className={styles.container} ref={gridRef}>
      <GridLayout
        className={styles.editDashboardLayout}
        cols={MODEL_DASHBOARD_COLUMNS}
        compactType="vertical"
        draggableHandle={`.${styles.panelDragHandle}`}
        isDraggable
        isResizable
        layout={layout}
        margin={LAYOUT_SETTING_MARGIN}
        onLayoutChange={handleLayoutChange}
        preventCollision={false}
        resizeHandle={<div className={styles.resizableHandle} />}
        rowHeight={ROW_HEIGHT}
        width={containerWidth}
      >
        {panels.map(panel => {
          // GridLayout needs a non-component as child
          // so we need to wrap the PanelGridItem in a div
          return (
            <div key={panel.id} className={styles.gridItem}>
              <PanelGridItem panel={panel} sectionId={sectionId} />
            </div>
          );
        })}
      </GridLayout>
    </div>
  );
}

export const PanelGrid = React.memo(PanelGridComponent);

function convertLayout(layout: Layout[]): PanelSectionLayout {
  return layout.map(item => ({
    i: item.i,
    x: item.x,
    y: item.y,
    h: item.h,
    w: item.w
  }));
}
